import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/Download";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useQueries, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  fetchVirtualOfficeAnalyticsLive,
  fetchVirtualOfficeFloorTime,
  fetchVirtualOfficeMySession,
  fetchVirtualOfficePresence,
  joinVirtualOfficeRoom,
  leaveVirtualOfficeRoom,
  listVirtualOfficeRooms,
  patchVirtualOfficePresence,
  patchVirtualOfficePresenceOrRejoin,
  walkUpVirtualOffice,
  downloadVirtualOfficeFloorTimeCsv,
  listVirtualOfficeHuddles,
  createVirtualOfficeHuddle,
  joinVirtualOfficeHuddle,
  consentTimeClock,
  fetchTimeClockPolicy,
} from "../../api/client";
import {
  isClockPolicy,
  isTimeClockConsentRequiredError,
  readFetchDataError,
  timeClockCopy,
  virtualOfficeCopy,
} from "./attendanceCopy";
import { jornadaAlertsCopy } from "./jornadaAlertsCopy";
import { useVirtualOfficeJornadaAlerts } from "./useVirtualOfficeJornadaAlerts";
import VirtualOfficeJornadaAlerts from "./VirtualOfficeJornadaAlerts";
import { VirtualOfficeHuddleCard } from "../../components/VirtualOfficeHuddleCard";
import { openAgentVideoJoinUrl } from "../../utils/activeVideoUi";
import {
  publishVirtualOfficePauseTracks,
  publishVirtualOfficeResumeTracks,
  readVirtualOfficeSession,
  subscribeVirtualOfficeBridge,
  writeVirtualOfficeSession,
} from "../../utils/virtualOfficeSessionBridge";
import { useAuthContext } from "../../context/AuthContext";
import { useSocketContext } from "../../context/SocketContext";
import { useTeamSocketPresence } from "../../hooks/useTeamSocketPresence";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import TeamPresenceTabBreakdown from "../../components/TeamPresenceTabBreakdown";
import { getTeamPresenceScreenTitle } from "../../utils/teamPresenceScreenTitle";
import { parsePresencePayload } from "./virtualOfficePresenceAlerts";
import { useVirtualOfficePresenceAlerts } from "./useVirtualOfficePresenceAlerts";
import {
  floorJoinCaption,
  isVoCommBusyStatus,
  isVoCommPausedStatus,
  resolveClockTimezone,
  voCommStatusHint,
  voCommStatusLabel,
  voCommStatusOptions,
  voCommStatusPauseTitle,
} from "./voCommunicationStatus";

async function requestGeo() {
  if (!navigator?.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  });
}

const STATUS_ORDER = { present: 0, focus: 1, snack: 2, lunch: 3, in_other_meeting: 4, away: 5 };

function statusMeta(status) {
  if (status === "in_other_meeting") {
    return { color: "warning", label: voCommStatusLabel(status), Icon: EventBusyOutlinedIcon };
  }
  if (status === "focus") {
    return { color: "info", label: voCommStatusLabel(status), Icon: CenterFocusStrongOutlinedIcon };
  }
  if (status === "lunch") {
    return { color: "warning", label: voCommStatusLabel(status), Icon: RestaurantOutlinedIcon };
  }
  if (status === "snack") {
    return { color: "warning", label: voCommStatusLabel(status), Icon: LocalCafeOutlinedIcon };
  }
  if (status === "away") {
    return { color: "default", label: voCommStatusLabel(status), Icon: PersonOffOutlinedIcon };
  }
  return { color: "success", label: voCommStatusLabel(status), Icon: GroupsOutlinedIcon };
}

function statusChip(status, otherTitle) {
  const meta = statusMeta(status);
  const label =
    status === "in_other_meeting" && otherTitle ? `Em reunião: ${otherTitle}` : meta.label;
  return (
    <Chip
      size="small"
      color={meta.color}
      icon={<meta.Icon sx={{ "&&": { fontSize: 14 } }} />}
      label={label}
      sx={{ fontWeight: 600, maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
    />
  );
}

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function friendlyPresenceTitle(path, tApp, segment) {
  const title = getTeamPresenceScreenTitle(path, tApp, segment);
  if (typeof title === "string" && title.startsWith("/")) {
    if (title.includes("video")) return "Chamada de vídeo";
    return "Outra tela";
  }
  return title;
}

const HUDDLE_TITLE_PRESETS = ["Café", "Reunião rápida", "Conversa"];

function huddleVideoIdFromPerson(person) {
  const ref = person?.otherMeetingRef;
  const id = Number(ref?.videoRoomId || ref?.video_room_id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

function floorSnapshotPeopleTimezone(presenceByRoomId) {
  for (const list of presenceByRoomId?.values?.() || []) {
    const tz = (list || []).find((p) => p?.timezone)?.timezone;
    if (tz) return tz;
  }
  return "";
}

function occupancyFromPresence(list) {
  let available = 0;
  let focus = 0;
  let lunch = 0;
  let snack = 0;
  let meeting = 0;
  let away = 0;
  for (const p of list || []) {
    if (p.status === "focus") focus += 1;
    else if (p.status === "lunch") lunch += 1;
    else if (p.status === "snack") snack += 1;
    else if (p.status === "in_other_meeting") meeting += 1;
    else if (p.status === "away") away += 1;
    else available += 1;
  }
  return { total: (list || []).length, available, focus, lunch, snack, meeting, away };
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function VirtualOfficePresenceRow({
  person,
  socketTabs,
  presenceDetail,
  screenTitleForPath,
  livePresenceNowMs,
  clockTimezone,
  tLive,
  canCall,
  calling,
  onCall,
  isSelf,
  highlight = false,
}) {
  const uid = Number(person.userId);
  const det = presenceDetail;
  const tabs = Number(socketTabs) || 0;
  const x4Count = det?.xpro4Tabs?.length || 0;
  const compactSingleTab = x4Count === 1 && !(det?.legacyTabCount > 0);
  const busy = isVoCommBusyStatus(person.status);
  const meta = statusMeta(person.status);
  const callHint = busy
    ? person.status === "focus"
      ? "Colega concentrado — não perturbe"
      : person.status === "lunch"
        ? "Colega em horário de almoço"
        : person.status === "snack"
          ? "Colega no lanche"
          : "Indisponível no momento"
    : "Chamar para conversa privada";
  const connectedLine = floorJoinCaption({
    joinedAt: person.joinedAt,
    now: livePresenceNowMs,
    timeZone: clockTimezone || person.timezone,
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        borderLeftWidth: 4,
        borderLeftStyle: "solid",
        borderLeftColor: (th) =>
          meta.color === "default" ? th.palette.text.disabled : `${meta.color}.main`,
        boxShadow: (th) =>
          highlight
            ? `0 0 0 2px ${th.palette.success.main}, 0 8px 24px ${alpha(th.palette.success.main, 0.28)}`
            : th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.22)}`
              : `0 1px 2px ${alpha(th.palette.common.black, 0.05)}`,
        transform: highlight ? "translateY(-1px)" : "none",
        transition: "box-shadow 240ms ease, transform 240ms ease, background-color 240ms ease",
        bgcolor: (th) =>
          highlight
            ? alpha(th.palette.success.main, th.palette.mode === "dark" ? 0.2 : 0.12)
            : alpha(
                meta.color === "default" ? th.palette.action.active : th.palette[meta.color].main,
                th.palette.mode === "dark" ? 0.12 : 0.05
              ),
      }}
    >
      <Stack spacing={1.25} sx={{ p: 1.5, minWidth: 0, height: "100%" }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: (th) =>
                  meta.color === "default" ? th.palette.text.disabled : th.palette[meta.color].main,
                boxShadow: (th) => `0 0 0 2px ${th.palette.background.paper}`,
                width: 10,
                height: 10,
                borderRadius: "50%",
              },
            }}
          >
            <Box sx={{ position: "relative" }}>
            <Avatar
              src={person.userPhoto || undefined}
              sx={{
                width: 44,
                height: 44,
                fontSize: "0.9rem",
                fontWeight: 700,
                border: 2,
                borderColor: (th) =>
                  person.isBirthday
                    ? "#e8a0bc"
                    : meta.color === "default"
                      ? th.palette.divider
                      : `${meta.color}.main`,
                bgcolor: (th) => alpha(th.palette.background.paper, 0.9),
              }}
            >
              {initialsFromName(person.userName || `Usuário ${uid}`)}
            </Avatar>
            {person.isBirthday ? (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -6,
                  fontSize: "0.95rem",
                  lineHeight: 1,
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
                }}
              >
                🎂
              </Box>
            ) : null}
            </Box>
          </Badge>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} noWrap title={person.userName || undefined} sx={{ lineHeight: 1.3 }}>
              {person.userName || `Usuário ${uid}`}
              {person.isBirthday ? " · aniversário" : ""}
              {isSelf ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  (você)
                </Typography>
              ) : null}
            </Typography>
            {person.userCargo ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {person.userCargo}
              </Typography>
            ) : null}
            {person.status === "in_other_meeting" && person.otherMeetingTitle ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {person.otherMeetingTitle}
              </Typography>
            ) : connectedLine ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {connectedLine}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {statusChip(person.status, person.otherMeetingTitle)}
          {!isSelf && canCall ? (
            <Tooltip title={callHint}>
              <span>
                <Button
                  size="small"
                  variant={busy ? "outlined" : "contained"}
                  startIcon={<CallOutlinedIcon />}
                  disabled={busy || calling}
                  onClick={() => onCall?.(uid)}
                  sx={{ ml: "auto" }}
                >
                  {calling ? "Chamando…" : "Chamar"}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>

        {tabs > 0 ? (
          <TeamPresenceTabBreakdown
            show
            socketTabs={tabs}
            presenceDetail={det}
            tLive={tLive}
            screenTitleForPath={screenTitleForPath}
            livePresenceNowMs={livePresenceNowMs}
            density="dense"
            compactSingleTab={compactSingleTab}
            showPath={false}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Sem abas Xpro detectadas agora.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function ManagementTab() {
  const { userData } = useAuthContext();
  const { t: tLive } = useTranslation("settings");
  const { t: tApp } = useTranslation("app");
  const { segment } = useWorkspaceSegment();
  const { tabsByUserId, presenceDetailByUserId } = useTeamSocketPresence(userData);
  const [from, setFrom] = useState(() => daysAgoIso(7));
  const [to, setTo] = useState(() => todayIso());
  const [livePresenceNowMs, setLivePresenceNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setLivePresenceNowMs(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);
  const screenTitleForPath = useCallback(
    (path) => friendlyPresenceTitle(path, tApp, segment),
    [tApp, segment]
  );
  const liveQuery = useQuery(["virtual-office-analytics-live"], async () => {
    const res = await fetchVirtualOfficeAnalyticsLive();
    return res?.data?.data || res?.data || [];
  });
  const floorQuery = useQuery(
    ["virtual-office-floor-time", from, to],
    async () => {
      const res = await fetchVirtualOfficeFloorTime({ from, to });
      return res?.data?.data || res?.data || { aggregates: [], sessions: [] };
    },
    { enabled: Boolean(from && to) }
  );

  const handleExport = async () => {
    const res = await downloadVirtualOfficeFloorTimeCsv({ from, to });
    const text = typeof res?.data === "string" ? res.data : res?.data?.data || "";
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vo-floor-time-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={2.5}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Tempo no escritório virtual mede presença no piso (integração de equipes), não jornada.
        Quem tem horário legal continua em{" "}
        <Button component={RouterLink} to="/settings/time-clock" size="small" sx={{ ml: 0.5 }}>
          Controle de Ponto
        </Button>
        . Prestador e função de confiança não entram em AFD/espelho.
      </Alert>

      <Typography variant="subtitle1" fontWeight={800}>
        Ao vivo nas salas
      </Typography>
      {liveQuery.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack spacing={1.5}>
          {(liveQuery.data || []).map((row) => (
            <Paper
              key={row.room?.id}
              variant="outlined"
              sx={{ borderRadius: 2.5, p: 2, overflow: "hidden" }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <MeetingRoomOutlinedIcon color="primary" fontSize="small" />
                <Typography fontWeight={700}>{row.room?.nome}</Typography>
                <Chip
                  size="small"
                  label={`${row.totalCount || 0} no piso`}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${row.presentCount || 0} disponíveis/foco`}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              <Box
                sx={{
                  mt: 1.5,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 1.25,
                }}
              >
                {(row.presence || []).map((p) => {
                  const uid = Number(p.userId);
                  const tabs = tabsByUserId.get(uid) || 0;
                  const det = presenceDetailByUserId.get(uid);
                  return (
                    <Paper
                      key={p.id}
                      variant="outlined"
                      sx={{ p: 1.25, borderRadius: 2, bgcolor: "background.paper" }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Avatar src={p.userPhoto || undefined} sx={{ width: 32, height: 32, fontSize: "0.8rem" }}>
                          {initialsFromName(p.userName || `Usuário ${uid}`)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {p.userName || `Usuário ${uid}`}
                          </Typography>
                          {p.userCargo ? (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {p.userCargo}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                      {statusChip(p.status, p.otherMeetingTitle)}
                      {tabs > 0 ? (
                        <Box sx={{ mt: 1 }}>
                          <TeamPresenceTabBreakdown
                            show
                            socketTabs={tabs}
                            presenceDetail={det}
                            tLive={tLive}
                            screenTitleForPath={screenTitleForPath}
                            livePresenceNowMs={livePresenceNowMs}
                            density="dense"
                            compactSingleTab={(det?.xpro4Tabs?.length || 0) === 1}
                            showPath={false}
                          />
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Sem abas Xpro detectadas.
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
              {!row.presence?.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Ninguém presente.
                </Typography>
              ) : null}
            </Paper>
          ))}
        </Stack>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        sx={{ mt: 1 }}
      >
        <TextField
          label="De"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => void handleExport()}
        >
          Exportar CSV
        </Button>
      </Stack>

      <Typography variant="subtitle1" fontWeight={800}>
        Tempo no piso (agregado)
      </Typography>
      {floorQuery.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <List dense disablePadding>
            {(floorQuery.data?.aggregates || []).map((a) => (
              <ListItem key={a.userId} divider sx={{ py: 1.25 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 36, height: 36, fontWeight: 700 }}>
                    {initialsFromName(a.userName || `Usuário ${a.userId}`)}
                  </Avatar>
                </ListItemAvatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>
                    {a.userName || `Usuário ${a.userId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.sessions} sessões · {a.minutesOnFloor} min no escritório
                  </Typography>
                </Box>
              </ListItem>
            ))}
            {!floorQuery.data?.aggregates?.length ? (
              <ListItem>
                <Typography variant="body2" color="text.secondary">
                  Sem sessões no período.
                </Typography>
              </ListItem>
            ) : null}
          </List>
        </Paper>
      )}
    </Stack>
  );
}

export default function VirtualOfficePage() {
  const { userData } = useAuthContext();
  const { t: tLive } = useTranslation("settings");
  const { t: tApp } = useTranslation("app");
  const { segment } = useWorkspaceSegment();
  const { tabsByUserId, presenceDetailByUserId } = useTeamSocketPresence(userData);
  const { socket } = useSocketContext() || {};
  const qc = useQueryClient();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(
    () =>
      scopeSetFromPermissions(permData, {
        ...userData,
        master: isMasterUser ? 1 : 0,
      }),
    [permData, userData, isMasterUser]
  );
  const canManage = can(scopeSet, "virtual_office", "manage");
  const myUserId = Number(userData?.id) || 0;

  const [tab, setTab] = useState(0);
  const [joiningId, setJoiningId] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const [activeRoomId, setActiveRoomId] = useState(() => readVirtualOfficeSession()?.roomId || null);
  const [sessionJoinedAt, setSessionJoinedAt] = useState(
    () => readVirtualOfficeSession()?.joinedAt || null
  );
  const [myStatus, setMyStatus] = useState(() => readVirtualOfficeSession()?.status || "present");
  const jornadaRefreshRef = useRef(async () => null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [error, setError] = useState("");
  const [presenceSearch, setPresenceSearch] = useState("");
  const [pausedOverlay, setPausedOverlay] = useState(() => {
    const s = readVirtualOfficeSession();
    return s?.status === "in_other_meeting" ? s.otherMeetingTitle || "Outra reunião" : "";
  });
  const [livePresenceNowMs, setLivePresenceNowMs] = useState(() => Date.now());
  const [huddleDialogOpen, setHuddleDialogOpen] = useState(false);
  const [huddleTitle, setHuddleTitle] = useState("Café");
  const [huddleVisibility, setHuddleVisibility] = useState("public");
  const [huddleBusy, setHuddleBusy] = useState(false);
  const [huddleActingId, setHuddleActingId] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [pendingJoinRoom, setPendingJoinRoom] = useState(null);
  const [consentInfo, setConsentInfo] = useState(null);
  const [attendancePolicy, setAttendancePolicy] = useState(
    () => readVirtualOfficeSession()?.attendancePolicy || null
  );
  const [joinScheduleStatus, setJoinScheduleStatus] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setLivePresenceNowMs(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const syncSession = () => {
      qc.invalidateQueries(["virtual-office-my-session"]);
      qc.invalidateQueries(["virtual-office-presence"]);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncSession();
    };
    window.addEventListener("focus", syncSession);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", syncSession);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [qc]);

  const screenTitleForPath = useCallback(
    (path) => friendlyPresenceTitle(path, tApp, segment),
    [tApp, segment]
  );

  const roomsQuery = useQuery(["virtual-office-rooms"], async () => {
    const res = await listVirtualOfficeRooms();
    return res?.data?.data || res?.data || [];
  });

  const mySessionQuery = useQuery(
    ["virtual-office-my-session"],
    async () => {
      const res = await fetchVirtualOfficeMySession();
      return res?.data?.data || res?.data || null;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: 20000,
      cacheTime: 0,
    }
  );

  const rooms = useMemo(() => roomsQuery.data || [], [roomsQuery.data]);
  const policyQuery = useQuery(
    ["time-clock-policy-self"],
    async () => {
      const res = await fetchTimeClockPolicy();
      return res?.data?.data || res?.data || null;
    },
    { staleTime: 30_000 }
  );

  const sessionPolicy =
    mySessionQuery.data?.attendancePolicy || attendancePolicy || policyQuery.data || null;
  const voCopy = useMemo(() => virtualOfficeCopy(sessionPolicy), [sessionPolicy]);
  const clockCopy = useMemo(() => timeClockCopy(sessionPolicy), [sessionPolicy]);
  const consentFromPolicy =
    policyQuery.data?.consent || sessionPolicy?.consent || attendancePolicy?.consent || null;
  const needsConsent = Boolean(
    isClockPolicy(sessionPolicy || policyQuery.data) &&
      policyQuery.data?.consented === false &&
      (consentFromPolicy?.version || consentFromPolicy?.body)
  );

  useEffect(() => {
    const next = mySessionQuery.data?.attendancePolicy || policyQuery.data;
    if (next) setAttendancePolicy(next);
  }, [mySessionQuery.data, policyQuery.data]);

  const presenceQueries = useQueries(
    rooms.map((room) => ({
      queryKey: ["virtual-office-presence", room.id],
      queryFn: async () => {
        const res = await fetchVirtualOfficePresence(room.id);
        return parsePresencePayload(res);
      },
      refetchInterval: 8000,
      enabled: Boolean(room?.id),
    }))
  );

  const presenceByRoomId = useMemo(() => {
    const map = new Map();
    rooms.forEach((room, idx) => {
      map.set(Number(room.id), presenceQueries[idx]?.data?.people || []);
    });
    return map;
  }, [rooms, presenceQueries]);

  const clockTimezone = useMemo(() => {
    const fromPresence = presenceQueries.find((q) => q?.data?.timezone)?.data?.timezone;
    const fromPerson = floorSnapshotPeopleTimezone(presenceByRoomId);
    return resolveClockTimezone(
      fromPresence,
      fromPerson,
      joinScheduleStatus?.timezone,
      mySessionQuery.data?.scheduleStatus?.timezone
    );
  }, [presenceQueries, presenceByRoomId, joinScheduleStatus, mySessionQuery.data]);

  const activityByRoomId = useMemo(() => {
    const map = new Map();
    rooms.forEach((room, idx) => {
      map.set(Number(room.id), presenceQueries[idx]?.data?.activity || []);
    });
    return map;
  }, [rooms, presenceQueries]);

  const floorSnapshot = useMemo(() => {
    const people = [];
    const activity = [];
    rooms.forEach((room) => {
      const rid = Number(room.id);
      people.push(...(presenceByRoomId.get(rid) || []));
      activity.push(...(activityByRoomId.get(rid) || []));
    });
    return { people, activity };
  }, [rooms, presenceByRoomId, activityByRoomId]);

  const presenceReady =
    !roomsQuery.isLoading &&
    (rooms.length === 0 || presenceQueries.every((q) => q.isFetched));

  const { alerts: presenceAlerts, highlightUserId, dismissAlert } = useVirtualOfficePresenceAlerts({
    people: floorSnapshot.people,
    activity: floorSnapshot.activity,
    selfUserId: myUserId,
    socket,
    enabled: presenceReady,
  });

  const huddleQueries = useQueries(
    rooms.map((room) => ({
      queryKey: ["virtual-office-huddles", room.id],
      queryFn: async () => {
        const res = await listVirtualOfficeHuddles(room.id);
        return res?.data?.data || res?.data || [];
      },
      refetchInterval: 15000,
      enabled: Boolean(room?.id),
    }))
  );

  const huddlesByRoomId = useMemo(() => {
    const map = new Map();
    rooms.forEach((room, idx) => {
      map.set(Number(room.id), huddleQueries[idx]?.data || []);
    });
    return map;
  }, [rooms, huddleQueries]);

  useEffect(() => {
    if (!socket) return undefined;
    const onVideoRoom = (raw) => {
      let payload = raw;
      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
        } catch {
          payload = null;
        }
      }
      const action = String(payload?.action || "");
      const ctx = String(payload?.callContext || "").toLowerCase();
      if (
        ctx === "vo_huddle" ||
        ctx === "vo_private" ||
        ctx === "virtual_office" ||
        action === "huddle_updated" ||
        action === "huddle_join_request" ||
        action === "huddle_join_resolved" ||
        action === "vo_presence"
      ) {
        qc.invalidateQueries(["virtual-office-huddles"]);
        qc.invalidateQueries(["virtual-office-presence"]);
      }
    };
    socket.on("video_room", onVideoRoom);
    return () => socket.off("video_room", onVideoRoom);
  }, [socket, qc]);

  const clearLocalOfficeSession = useCallback(() => {
    setActiveRoomId(null);
    setSessionJoinedAt(null);
    setPausedOverlay("");
    setMyStatus("present");
    if (readVirtualOfficeSession()) writeVirtualOfficeSession(null);
  }, []);

  useEffect(() => {
    if (!mySessionQuery.isFetched || mySessionQuery.isFetching || joiningId) return;
    const session = mySessionQuery.data;
    const rid = Number(session?.roomId || session?.room_id);
    if (!Number.isFinite(rid) || rid <= 0) {
      if (activeRoomId) clearLocalOfficeSession();
      return;
    }
    if (Number(activeRoomId) === rid) return;
    setActiveRoomId(rid);
    setSessionJoinedAt(session.joined_at ? new Date(session.joined_at).getTime() : Date.now());
    setMyStatus(session.status || "present");
    writeVirtualOfficeSession({
      roomId: rid,
      roomName: session.roomName || session.room_nome,
      videoToken: session.video?.publicToken,
      status: session.status || "present",
      joinedAt: session.joined_at ? new Date(session.joined_at).getTime() : Date.now(),
      attendancePolicy: session.attendancePolicy || sessionPolicy || attendancePolicy || null,
    });
  }, [
    mySessionQuery.data,
    mySessionQuery.isFetched,
    mySessionQuery.isFetching,
    joiningId,
    activeRoomId,
    sessionPolicy,
    attendancePolicy,
    clearLocalOfficeSession,
  ]);

  useEffect(() => {
    return subscribeVirtualOfficeBridge((msg) => {
      if (!msg) return;
      if (msg.type === "other_meeting_started") {
        setPausedOverlay(msg.title || "Outra reunião");
        setMyStatus("in_other_meeting");
        publishVirtualOfficePauseTracks({ title: msg.title });
        void patchVirtualOfficePresenceOrRejoin(activeRoomId || readVirtualOfficeSession()?.roomId, {
          status: "in_other_meeting",
          otherMeetingTitle: msg.title,
          otherMeetingRef: msg.ref || null,
        }).catch(() => {});
        if (activeRoomId) {
          qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
        }
      }
      if (msg.type === "other_meeting_ended") {
        setPausedOverlay("");
        setMyStatus("present");
        publishVirtualOfficeResumeTracks();
        void patchVirtualOfficePresence({ status: "present" }).catch(() => {});
        if (activeRoomId) {
          qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
        }
      }
      if (msg.type === "session" && msg.session?.roomId) {
        setActiveRoomId(msg.session.roomId);
        setSessionJoinedAt(msg.session.joinedAt || Date.now());
        qc.invalidateQueries(["virtual-office-my-session"]);
      }
      if (msg.type === "session" && !msg.session) {
        setActiveRoomId(null);
        setSessionJoinedAt(null);
        setPausedOverlay("");
        setMyStatus("present");
        qc.invalidateQueries(["virtual-office-my-session"]);
        qc.invalidateQueries(["virtual-office-presence"]);
      }
    });
  }, [activeRoomId, qc]);

  const openConsentDialog = useCallback(
    (room, extraConsent = null) => {
      setConsentInfo(
        extraConsent ||
          consentFromPolicy ||
          policyQuery.data?.consent ||
          sessionPolicy?.consent ||
          attendancePolicy?.consent ||
          null
      );
      if (room) setPendingJoinRoom(room);
      setConsentOpen(true);
    },
    [attendancePolicy, consentFromPolicy, policyQuery.data, sessionPolicy]
  );

  const handleJoin = useCallback(
    async (room, { skipConsentGate } = {}) => {
      setError("");
      if (needsConsent && !skipConsentGate) {
        openConsentDialog(room);
        return;
      }
      setJoiningId(room.id);
      try {
        const geo = await requestGeo();
        const res = await joinVirtualOfficeRoom(room.id, {
          geo,
          deviceInfo: { ua: navigator.userAgent, source: "virtual_office_page" },
        });
        const data = res?.data?.data || res?.data || {};
        const joinUrl = data?.video?.joinUrl;
        const joinedAt = Date.now();
        const policy = data?.attendancePolicy || sessionPolicy;
        setAttendancePolicy(policy || null);
        if (data?.scheduleStatus) setJoinScheduleStatus(data.scheduleStatus);
        writeVirtualOfficeSession({
          roomId: room.id,
          roomName: room.nome,
          videoToken: data?.video?.publicToken,
          status: "present",
          joinedAt,
          attendancePolicy: policy || null,
        });
        setActiveRoomId(room.id);
        setSessionJoinedAt(joinedAt);
        setMyStatus("present");
        if (joinUrl) {
          openAgentVideoJoinUrl(joinUrl, {
            context: "virtual_office",
            name: userData?.nome || userData?.name || "",
            voRoomId: room.id,
          });
        }
        qc.invalidateQueries(["virtual-office-presence", room.id]);
        await qc.invalidateQueries(["virtual-office-my-session"]);
        qc.invalidateQueries(["time-clock-policy-self"]);
      } catch (e) {
        const { data: payload, message } = readFetchDataError(e);
        if (isTimeClockConsentRequiredError(e)) {
          if (payload.attendancePolicy) setAttendancePolicy(payload.attendancePolicy);
          openConsentDialog(room, payload.consent || payload.attendancePolicy?.consent || null);
          setError("");
        } else {
          setError(message || "Não foi possível ingressar.");
        }
      } finally {
        setJoiningId(null);
      }
    },
    [needsConsent, openConsentDialog, qc, sessionPolicy, userData]
  );

  const acceptConsentAndJoin = useCallback(async () => {
    const version =
      consentInfo?.version ||
      consentFromPolicy?.version ||
      policyQuery.data?.consent?.version ||
      sessionPolicy?.consent?.version;
    if (!version) {
      setError("Não há texto de ciência para este regime.");
      return;
    }
    setConsentBusy(true);
    setError("");
    try {
      await consentTimeClock({ textVersion: version });
      setConsentOpen(false);
      const room = pendingJoinRoom;
      setPendingJoinRoom(null);
      setConsentInfo(null);
      await qc.invalidateQueries(["time-clock-policy-self"]);
      if (room) await handleJoin(room, { skipConsentGate: true });
    } catch (e) {
      const { message } = readFetchDataError(e);
      setError(message || "Não foi possível registrar a ciência.");
    } finally {
      setConsentBusy(false);
    }
  }, [
    consentFromPolicy,
    consentInfo,
    handleJoin,
    pendingJoinRoom,
    policyQuery.data,
    qc,
    sessionPolicy,
  ]);

  const handleReopenVideo = useCallback(async () => {
    setError("");
    try {
      const res = await fetchVirtualOfficeMySession();
      const session = res?.data?.data || res?.data || null;
      const joinUrl = session?.video?.joinUrl;
      const rid = Number(session?.roomId || session?.room_id || activeRoomId);
      if (!joinUrl || !rid) {
        setError("Sessão aberta sem link de vídeo. Ingresse novamente pela sala.");
        return;
      }
      if (session?.attendancePolicy) setAttendancePolicy(session.attendancePolicy);
      writeVirtualOfficeSession({
        ...(readVirtualOfficeSession() || {}),
        roomId: rid,
        roomName: session?.roomName || session?.room_nome,
        videoToken: session?.video?.publicToken,
        status: session?.status || "present",
        attendancePolicy: session?.attendancePolicy || sessionPolicy || attendancePolicy || null,
      });
      openAgentVideoJoinUrl(joinUrl, {
        context: "virtual_office",
        name: userData?.nome || userData?.name || "",
        voRoomId: rid,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível reabrir o vídeo.");
    }
  }, [activeRoomId, userData, sessionPolicy, attendancePolicy]);

  const handleStatusChange = useCallback(
    async (next) => {
      if (myStatus === "in_other_meeting") {
        setError("Status bloqueado enquanto você está em outra reunião ou conversa privada.");
        return;
      }
      setError("");
      try {
        await patchVirtualOfficePresence({ status: next });
        setMyStatus(next);
        const cur = readVirtualOfficeSession();
        if (cur) writeVirtualOfficeSession({ ...cur, status: next });
        if (isVoCommPausedStatus(next)) {
          publishVirtualOfficePauseTracks({
            title: voCommStatusPauseTitle(next),
          });
        } else if (next === "present") {
          publishVirtualOfficeResumeTracks();
        }
        if (activeRoomId) qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
        void jornadaRefreshRef.current();
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Erro ao atualizar status.");
      }
    },
    [activeRoomId, qc, myStatus]
  );

  const handleWalkUp = useCallback(
    async (roomId, targetUserId) => {
      setError("");
      setCallingId(targetUserId);
      try {
        const res = await walkUpVirtualOffice(roomId, { targetUserId });
        const data = res?.data?.data || res?.data || {};
        const joinUrl = data?.video?.joinUrl;
        if (joinUrl) {
          publishVirtualOfficePauseTracks({ title: "Conversa privada" });
          openAgentVideoJoinUrl(joinUrl, {
            context: "vo_private",
            name: userData?.nome || userData?.name || "",
          });
        }
        qc.invalidateQueries(["virtual-office-presence", roomId]);
        qc.invalidateQueries(["virtual-office-huddles", roomId]);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Não foi possível chamar o colega.");
      } finally {
        setCallingId(null);
      }
    },
    [qc, userData]
  );

  const openHuddleVideo = useCallback(
    (joinUrl, title, context = "vo_huddle") => {
      if (!joinUrl) return;
      publishVirtualOfficePauseTracks({ title: title || "Sala do escritório" });
      openAgentVideoJoinUrl(joinUrl, {
        context,
        name: userData?.nome || userData?.name || "",
      });
    },
    [userData]
  );

  const handleCreateHuddle = useCallback(async () => {
    if (!activeRoomId) return;
    setHuddleBusy(true);
    setError("");
    try {
      const res = await createVirtualOfficeHuddle(activeRoomId, {
        title: huddleTitle,
        visibility: huddleVisibility,
      });
      const data = res?.data?.data || res?.data || {};
      const joinUrl = data?.video?.joinUrl;
      setHuddleDialogOpen(false);
      openHuddleVideo(joinUrl, huddleTitle, huddleVisibility === "public" ? "vo_huddle" : "vo_private");
      qc.invalidateQueries(["virtual-office-huddles", activeRoomId]);
      qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível abrir a sala.");
    } finally {
      setHuddleBusy(false);
    }
  }, [activeRoomId, huddleTitle, huddleVisibility, openHuddleVideo, qc]);

  const handleHuddleJoinOrRequest = useCallback(
    async (huddle) => {
      setError("");
      setHuddleActingId(huddle.videoRoomId || huddle.id);
      try {
        const res = await joinVirtualOfficeHuddle(huddle.videoRoomId || huddle.id);
        const data = res?.data?.data || res?.data || {};
        if (data.status === "joined" && data.joinUrl) {
          openHuddleVideo(
            data.joinUrl,
            huddle.title,
            huddle.visibility === "public" ? "vo_huddle" : "vo_private"
          );
        }
        qc.invalidateQueries(["virtual-office-huddles"]);
        qc.invalidateQueries(["virtual-office-presence"]);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Não foi possível entrar na sala.");
      } finally {
        setHuddleActingId(null);
      }
    },
    [openHuddleVideo, qc]
  );

  const doLeave = useCallback(
    async (leaveKind) => {
      if (!activeRoomId) return;
      setError("");
      try {
        const geo = await requestGeo();
        const res = await leaveVirtualOfficeRoom(activeRoomId, {
          leaveKind,
          geo,
          deviceInfo: { ua: navigator.userAgent },
        });
        const payload = res?.data?.data || res?.data || {};
        if (leaveKind === "break" && payload.stayed) {
          setMyStatus("lunch");
          setLeaveOpen(false);
          const cur = readVirtualOfficeSession();
          if (cur) writeVirtualOfficeSession({ ...cur, status: "lunch" });
          if (isVoCommPausedStatus("lunch")) {
            publishVirtualOfficePauseTracks({
              title: voCommStatusPauseTitle("lunch"),
            });
          }
          qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
          qc.invalidateQueries(["virtual-office-my-session"]);
          if (payload.scheduleStatus) setJoinScheduleStatus(payload.scheduleStatus);
          void jornadaRefreshRef.current();
          return;
        }
        writeVirtualOfficeSession(null);
        setActiveRoomId(null);
        setSessionJoinedAt(null);
        setPausedOverlay("");
        setMyStatus("present");
        setLeaveOpen(false);
        setJoinScheduleStatus(null);
        qc.invalidateQueries(["virtual-office-rooms"]);
        qc.invalidateQueries(["virtual-office-presence", activeRoomId]);
        qc.invalidateQueries(["virtual-office-huddles", activeRoomId]);
        qc.invalidateQueries(["virtual-office-my-session"]);
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Erro ao registrar saída.");
      }
    },
    [activeRoomId, qc]
  );

  const jornadaEnabled = isClockPolicy(sessionPolicy || policyQuery.data);
  const jornadaAlerts = useVirtualOfficeJornadaAlerts({
    enabled: jornadaEnabled,
    present: Boolean(activeRoomId),
    initialSchedule:
      joinScheduleStatus ||
      mySessionQuery.data?.scheduleStatus ||
      policyQuery.data?.scheduleStatus ||
      null,
  });
  jornadaRefreshRef.current = jornadaAlerts.refresh;
  const jornadaCopy = useMemo(
    () => (jornadaAlerts.schedule?.applies ? jornadaAlertsCopy(jornadaAlerts.schedule) : null),
    [jornadaAlerts.schedule]
  );

  const sortAndFilterPresence = useCallback(
    (list) => {
      const q = String(presenceSearch || "")
        .trim()
        .toLowerCase();
      return [...(list || [])]
        .filter((p) => {
          if (!q) return true;
          return String(p.userName || "")
            .toLowerCase()
            .includes(q);
        })
        .sort((a, b) => {
          const oa = STATUS_ORDER[a.status] ?? 9;
          const ob = STATUS_ORDER[b.status] ?? 9;
          if (oa !== ob) return oa - ob;
          return String(a.userName || "").localeCompare(String(b.userName || ""), "pt-BR");
        });
    },
    [presenceSearch]
  );

  const activeRoom = rooms.find((r) => Number(r.id) === Number(activeRoomId));
  const floorOccupancy = useMemo(() => {
    const merged = [];
    rooms.forEach((room) => {
      merged.push(...(presenceByRoomId.get(Number(room.id)) || []));
    });
    return occupancyFromPresence(merged);
  }, [rooms, presenceByRoomId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1120, mx: "auto", width: "100%" }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 2.5,
          borderRadius: 3,
          overflow: "hidden",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.16 : 0.08)} 0%, ${alpha(th.palette.background.paper, 0.9)} 62%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          sx={{ p: { xs: 2, sm: 2.5 } }}
        >
          <Stack direction="row" spacing={1.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: (th) => alpha(th.palette.primary.main, 0.14),
                color: "primary.main",
              }}
            >
              <DesktopWindowsOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.3, lineHeight: 1.2 }}>
                Escritório Virtual
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 640 }}>
                {voCopy.subtitle}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<GroupsOutlinedIcon />}
              label={`${floorOccupancy.total} no piso`}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              color="success"
              variant="outlined"
              label={`${floorOccupancy.available} disponíveis`}
              sx={{ fontWeight: 700 }}
            />
            {floorOccupancy.focus ? (
              <Chip color="info" variant="outlined" label={`${floorOccupancy.focus} concentrados`} sx={{ fontWeight: 700 }} />
            ) : null}
            {floorOccupancy.lunch ? (
              <Chip color="warning" variant="outlined" label={`${floorOccupancy.lunch} no almoço`} sx={{ fontWeight: 700 }} />
            ) : null}
            {floorOccupancy.snack ? (
              <Chip color="warning" variant="outlined" label={`${floorOccupancy.snack} no lanche`} sx={{ fontWeight: 700 }} />
            ) : null}
          </Stack>
        </Stack>
        {canManage ? (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1.5,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.7),
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTab-root": { fontWeight: 700, minHeight: 46 },
            }}
          >
            <Tab label="Escritório" />
            <Tab label="Gestão" />
          </Tabs>
        ) : null}
      </Paper>

      {tab === 1 && canManage ? (
        <ManagementTab />
      ) : (
        <>
          {error ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setError("")}
              action={
                /ciência/i.test(error) || needsConsent ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => openConsentDialog(rooms[0] || pendingJoinRoom)}
                  >
                    {clockCopy.consentButton || "Registrar ciência"}
                  </Button>
                ) : null
              }
            >
              {error}
            </Alert>
          ) : null}

          {needsConsent && !activeRoomId ? (
            <Alert
              severity="warning"
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => openConsentDialog(rooms[0] || pendingJoinRoom)}
                >
                  {clockCopy.consentButton || "Registrar ciência"}
                </Button>
              }
            >
              Registre a ciência do controle de horário antes de ingressar.
            </Alert>
          ) : null}

          {voCopy.legalBanner ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {voCopy.legalBanner}
            </Alert>
          ) : null}

          {activeRoomId && jornadaCopy?.banner ? (
            <Alert
              severity={jornadaAlerts.schedule?.exitDue?.isDue ? "warning" : "info"}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {jornadaCopy.banner}
              {jornadaCopy.bankLine ? ` · ${jornadaCopy.bankLine}` : ""}
            </Alert>
          ) : null}

          {pausedOverlay ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Você está em outra reunião: <strong>{pausedOverlay}</strong>. Áudio e vídeo do
              escritório virtual estão pausados.
            </Alert>
          ) : null}

          {presenceAlerts.length ? (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {presenceAlerts.map((alert) => {
                const Icon =
                  alert.sound === "enter"
                    ? LoginIcon
                    : alert.sound === "leave_break"
                      ? FreeBreakfastOutlinedIcon
                      : alert.sound === "leave_exit"
                        ? LogoutIcon
                        : MeetingRoomOutlinedIcon;
                const severity =
                  alert.severity === "default" ? "info" : alert.severity;
                return (
                  <Collapse key={alert.key} in>
                    <Alert
                      severity={severity}
                      icon={<Icon />}
                      onClose={() => dismissAlert(alert.key)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 700,
                        ...(alert.sound === "leave_other"
                          ? {
                              bgcolor: (th) =>
                                alpha(th.palette.text.primary, th.palette.mode === "dark" ? 0.12 : 0.06),
                              color: "text.primary",
                              "& .MuiAlert-icon": { color: "text.secondary" },
                            }
                          : null),
                      }}
                    >
                      {alert.title}
                    </Alert>
                  </Collapse>
                );
              })}
            </Stack>
          ) : null}

          {activeRoomId ? (
            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: 2.5,
                p: 1.5,
                borderColor: (th) => alpha(th.palette.success.main, 0.4),
                bgcolor: (th) => alpha(th.palette.success.main, th.palette.mode === "dark" ? 0.14 : 0.08),
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800}>{voCopy.inCallTitle}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeRoom?.nome || "Sala ativa"}
                    {sessionJoinedAt
                      ? ` · ${floorJoinCaption({
                          joinedAt: sessionJoinedAt,
                          now: livePresenceNowMs,
                          timeZone: clockTimezone,
                        })}`
                      : ""}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VideocamOutlinedIcon />}
                    onClick={() => void handleReopenVideo()}
                  >
                    Voltar ao vídeo
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<LogoutIcon />}
                    onClick={() => setLeaveOpen(true)}
                  >
                    {voCopy.leaveButton}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {activeRoomId ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="vo-status-label">Meu status</InputLabel>
                <Select
                  labelId="vo-status-label"
                  label="Meu status"
                  value={myStatus === "in_other_meeting" ? "in_other_meeting" : myStatus}
                  disabled={myStatus === "in_other_meeting"}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === "in_other_meeting") return;
                    void handleStatusChange(next);
                  }}
                >
                  {voCommStatusOptions({
                    clockApplies: jornadaEnabled,
                    currentStatus: myStatus,
                  }).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                  {myStatus === "in_other_meeting" ? (
                    <MenuItem value="in_other_meeting" disabled>
                      {voCommStatusLabel("in_other_meeting")}
                    </MenuItem>
                  ) : null}
                </Select>
                {myStatus === "lunch" || myStatus === "snack" ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {voCommStatusHint(myStatus, { clockApplies: jornadaEnabled })}
                  </Typography>
                ) : null}
              </FormControl>
              <TextField
                size="small"
                placeholder="Buscar presentes…"
                value={presenceSearch}
                onChange={(e) => setPresenceSearch(e.target.value)}
                sx={{ flex: 1, bgcolor: "background.paper", borderRadius: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          ) : null}

          {roomsQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rooms.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Nenhuma sala disponível. Peça ao administrador para configurar em Configurações →
              Escritório Virtual.
            </Alert>
          ) : (
            <Stack spacing={2.5}>
              {rooms.map((room, roomIdx) => {
                const isActive = Number(activeRoomId) === Number(room.id);
                const rawPresence = presenceByRoomId.get(Number(room.id)) || [];
                const presence = sortAndFilterPresence(rawPresence);
                const rawCount = rawPresence.length;
                const occ = occupancyFromPresence(rawPresence);
                const presenceLoading = Boolean(presenceQueries[roomIdx]?.isLoading);
                return (
                  <Paper
                    key={room.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      borderColor: (th) =>
                        isActive ? alpha(th.palette.primary.main, 0.55) : th.palette.divider,
                      boxShadow: (th) =>
                        isActive
                          ? `0 8px 24px ${alpha(th.palette.primary.main, 0.1)}`
                          : th.palette.mode === "dark"
                            ? `0 1px 4px ${alpha("#000", 0.2)}`
                            : `0 1px 2px ${alpha(th.palette.common.black, 0.04)}`,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                      sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        background: (th) =>
                          isActive
                            ? `linear-gradient(90deg, ${alpha(th.palette.primary.main, 0.1)} 0%, ${alpha(th.palette.background.paper, 0.4)} 100%)`
                            : alpha(th.palette.action.hover, 0.35),
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Avatar
                          sx={{
                            bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                            color: "primary.main",
                            width: 44,
                            height: 44,
                          }}
                        >
                          <MeetingRoomOutlinedIcon />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography fontWeight={800}>{room.nome}</Typography>
                            {isActive ? (
                              <Chip size="small" color="primary" label="Você está aqui" sx={{ fontWeight: 700 }} />
                            ) : null}
                          </Stack>
                          {room.descricao ? (
                            <Typography variant="body2" color="text.secondary">
                              {room.descricao}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                            <Chip size="small" icon={<GroupsOutlinedIcon />} label={`${rawCount} presentes`} />
                            {occ.available ? (
                              <Chip size="small" color="success" variant="outlined" label={`${occ.available} disponíveis`} />
                            ) : null}
                            {occ.lunch ? (
                              <Chip size="small" color="warning" variant="outlined" label={`${occ.lunch} no almoço`} />
                            ) : null}
                            {occ.snack ? (
                              <Chip size="small" color="warning" variant="outlined" label={`${occ.snack} no lanche`} />
                            ) : null}
                          </Stack>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {isActive ? (
                          <>
                            <Button
                              variant="contained"
                              startIcon={<VideocamOutlinedIcon />}
                              onClick={() => void handleReopenVideo()}
                            >
                              Abrir vídeo
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setHuddleTitle("Café");
                                setHuddleVisibility("public");
                                setHuddleDialogOpen(true);
                              }}
                            >
                              Nova sala
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<LogoutIcon />}
                              onClick={() => setLeaveOpen(true)}
                            >
                              {voCopy.leaveButton}
                            </Button>
                          </>
                        ) : (
                          <Tooltip
                            title={
                              activeRoomId
                                ? "Saia da sala atual para ingressar em outra."
                                : voCopy.joinHint
                            }
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="large"
                                startIcon={<LoginIcon />}
                                disabled={Boolean(joiningId) || Boolean(activeRoomId)}
                                onClick={() => handleJoin(room)}
                              >
                                {joiningId === room.id ? voCopy.joining : voCopy.joinButton}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>

                    <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: (th) => alpha(th.palette.primary.main, 0.025) }}>
                      {(() => {
                        const huddles = huddlesByRoomId.get(Number(room.id)) || [];
                        const huddleUserIds = new Set(
                          huddles.flatMap((h) => (h.participants || []).map((p) => Number(p.userId)))
                        );
                        const floorPeople = presence.filter((p) => {
                          const hid = huddleVideoIdFromPerson(p);
                          return !hid && !huddleUserIds.has(Number(p.userId));
                        });
                        return (
                          <>
                      {huddles.length ? (
                        <Box sx={{ mb: 2.5 }}>
                          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.25 }}>
                            Salas e conversas
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(auto-fill, minmax(260px, 1fr))",
                              },
                              gap: 1.5,
                            }}
                          >
                            {huddles.map((huddle) => (
                              <VirtualOfficeHuddleCard
                                key={huddle.id || huddle.videoRoomId}
                                huddle={huddle}
                                canAct={isActive}
                                joining={huddleActingId === (huddle.videoRoomId || huddle.id) && huddle.canJoinDirect}
                                requesting={huddleActingId === (huddle.videoRoomId || huddle.id) && !huddle.canJoinDirect}
                                onJoin={(h) => void handleHuddleJoinOrRequest(h)}
                                onRequest={(h) => void handleHuddleJoinOrRequest(h)}
                              />
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.25 }}>
                        Quem está no piso
                      </Typography>
                      {presenceLoading && !floorPeople.length ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : floorPeople.length ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(auto-fill, minmax(280px, 1fr))",
                            },
                            gap: 1.5,
                          }}
                        >
                          {floorPeople.map((p) => {
                            const uid = Number(p.userId);
                            return (
                              <VirtualOfficePresenceRow
                                key={p.id}
                                person={p}
                                socketTabs={tabsByUserId.get(uid) || 0}
                                presenceDetail={presenceDetailByUserId.get(uid)}
                                screenTitleForPath={screenTitleForPath}
                                livePresenceNowMs={livePresenceNowMs}
                                clockTimezone={clockTimezone}
                                tLive={tLive}
                                canCall={isActive}
                                calling={callingId === uid}
                                isSelf={uid === myUserId}
                                highlight={uid === Number(highlightUserId)}
                                onCall={(targetId) => void handleWalkUp(room.id, targetId)}
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{
                            py: 5,
                            px: 2,
                            textAlign: "center",
                            borderStyle: "dashed",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          <GroupsOutlinedIcon color="disabled" sx={{ fontSize: 36, mb: 1 }} />
                          <Typography fontWeight={700}>
                            {presenceSearch ? "Nenhum presente corresponde à busca." : "Ninguém no piso agora"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {presenceSearch
                              ? "Tente outro nome."
                              : "Seja o primeiro a ingressar e abrir o escritório."}
                          </Typography>
                        </Paper>
                      )}
                          </>
                        );
                      })()}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </>
      )}

      <Dialog
        open={huddleDialogOpen}
        onClose={() => (huddleBusy ? null : setHuddleDialogOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Nova sala no escritório</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Como um café ou sala ao lado: pública, qualquer colega no piso entra. Privada, eles pedem
            para entrar.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            {HUDDLE_TITLE_PRESETS.map((preset) => (
              <Chip
                key={preset}
                icon={preset === "Café" ? <LocalCafeOutlinedIcon /> : <ForumOutlinedIcon />}
                label={preset}
                color={huddleTitle === preset ? "primary" : "default"}
                variant={huddleTitle === preset ? "filled" : "outlined"}
                onClick={() => setHuddleTitle(preset)}
              />
            ))}
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Nome da sala"
            value={huddleTitle}
            onChange={(e) => setHuddleTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="vo-huddle-vis">Visibilidade</InputLabel>
            <Select
              labelId="vo-huddle-vis"
              label="Visibilidade"
              value={huddleVisibility}
              onChange={(e) => setHuddleVisibility(e.target.value)}
            >
              <MenuItem value="public">Pública — qualquer pessoa do escritório entra</MenuItem>
              <MenuItem value="private">Privada — colegas pedem para entrar</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHuddleDialogOpen(false)} disabled={huddleBusy}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={huddleBusy || !String(huddleTitle || "").trim()}
            onClick={() => void handleCreateHuddle()}
          >
            {huddleBusy ? "Abrindo…" : "Abrir sala"}
          </Button>
        </DialogActions>
      </Dialog>
      <VirtualOfficeJornadaAlerts
        alerts={jornadaAlerts}
        onLeave={(kind) => void doLeave(kind)}
        onReturn={() => void handleStatusChange("present")}
      />
      <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{voCopy.leaveTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {voCopy.leaveBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          {voCopy.leaveBreak ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FreeBreakfastOutlinedIcon />}
              onClick={() => doLeave("break")}
            >
              {voCopy.leaveBreak}
            </Button>
          ) : null}
          <Button
            fullWidth
            variant="contained"
            color="warning"
            startIcon={<LogoutIcon />}
            onClick={() => doLeave("exit")}
          >
            {voCopy.leaveExit}
          </Button>
          <Button fullWidth onClick={() => setLeaveOpen(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={consentOpen} onClose={() => !consentBusy && setConsentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {consentInfo?.title || "Ciência do controle de horário"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
            {consentInfo?.body ||
              "É preciso registrar ciência do controle de horário antes da primeira marcação."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={consentBusy} onClick={() => setConsentOpen(false)}>
            Agora não
          </Button>
          <Button variant="contained" disabled={consentBusy} onClick={() => void acceptConsentAndJoin()}>
            {consentBusy ? "Registrando…" : "Li e concordo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
